import type React from "react";
import { useState, useRef, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Textarea,
  Avatar,
  Flex,
  useColorModeValue,
  ScaleFade,
  Portal,
  Spinner,
  Image,
  Link,
} from "@chakra-ui/react";
import { FiSend } from "react-icons/fi";
import { MdOutlineClose } from "react-icons/md";
import { theme } from "src/styles/theme";
import chatServiceInstance, {
  type SendMessageRequest,
} from "src/services/ChatBot/chatService";
import TypingIndicator from "./TypingIndicator";
import { useLoginAuth } from "src/context/loginContext";
import { UsuallyContext } from "src/context/usuallyContex";
import AvatarElo from "../../../assets/Avatar-Elo.png";
import AvatarEloAzul from "../../../assets/Avatar-Elo-Azul.png";
import { keyframes } from "@emotion/react";
import { marked } from "marked";
import { cookieKeys, getCookie } from "@helpers/cookies";

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

interface MessageContent {
  type: "text" | "image" | "link";
  content: string;
  url?: string;
}

type ChatBotProps = {};

// ─── hook: viewport size (reativo) ────────────────────────────────────────────
function useViewport() {
  const [size, setSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handler = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });

    // ResizeObserver é mais preciso que window.resize em mobile
    const ro = new ResizeObserver(handler);
    ro.observe(document.documentElement);

    window.addEventListener("orientationchange", handler);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", handler);
    };
  }, []);

  return size;
}

// ─── hook: detecção de toque ───────────────────────────────────────────────────
function useIsTouchDevice() {
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    setIsTouch("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);
  return isTouch;
}

const ChatBot: React.FC<ChatBotProps> = () => {
  const { isAuthenticated, accessChatBot } = useLoginAuth();
  const { isChatOpen, setIsChatOpen } = useContext(UsuallyContext);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue(theme.disable100, theme.disable100);
  const textColor = useColorModeValue(theme.text, "white");
  const botMessageBg = useColorModeValue(theme.disable100, theme.disable100);
  const userMessageBg = useColorModeValue(theme.primary, theme.primary);

  // Estado inicial (usa top/left agora)
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem("chatbotPosition");
    if (saved) return JSON.parse(saved);

    return { xPerc: 0.95, yPerc: 0.88 }; // canto inferior direito
  });

  const [dragging, setDragging] = useState(false);
  const [dragMoved, setDragMoved] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const viewport = useViewport();
  const isTouch = useIsTouchDevice();

  // Derivado: mobile = largura < 480px
  const isMobile = viewport.width < 480;

  const BUTTON_SIZE = isMobile ? 52 : 60;

  // Animações personalizadas
  const slideUpAndFade = keyframes`
   0% {
     opacity: 0;
     transform: translateY(20px) scale(0.95);
   }
   100% {
     opacity: 1;
     transform: translateY(0) scale(1);
   }
 `;

  const bounceIn = keyframes`
   0% {
     opacity: 0;
     transform: scale(0.3);
   }
   50% {
     transform: scale(1.05);
   }
   70% {
     transform: scale(0.9);
   }
   100% {
     opacity: 1;
     transform: scale(1);
   }
 `;

  const fadeInUp = keyframes`
   0% {
     opacity: 0;
     transform: translateY(30px);
   }
   100% {
     opacity: 1;
     transform: translateY(0);
   }
 `;

  // Animação para o indicador de digitação
  const fadeIn = keyframes`
   0% {
     opacity: 0;
     transform: translateY(10px);
   }
   100% {
     opacity: 1;
     transform: translateY(0);
   }
 `;

  // Mensagem inicial da Elô
  const initialMessage: Message = {
    id: "1",
    text: "Olá, eu sou a Elô, assistente virtual do Grupo Direcional, muito prazer!\n\nVocê precisa de ajuda com qual assunto?",
    isBot: true,
    timestamp: new Date(),
  };

  // Função para processar o texto e extrair URLs
  const parseMessageContent = (text: string): MessageContent[] => {
    return [
      {
        type: "text",
        content: text,
      },
    ];
  };

  const markdownToHtml = (markdown: string): string => {
    // 1. Corrigir links de imagem: [texto](url.jpg) => ![texto](url.jpg)
    const fixedMarkdown = markdown.replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+\.(?:jpg|jpeg|png|gif|webp))\)/gi,
      "![$1]($2)",
    );

    // 2. Converter markdown para HTML
    let html = marked.parse(fixedMarkdown, { breaks: true }) as string;

    // 3. Adicionar target="_blank" e rel em todos os links
    html = html.replace(
      /<a /g,
      '<a target="_blank" rel="noopener noreferrer" ',
    );

    // 4. Envelopar todas as imagens com links para abrir em nova aba
    html = html.replace(
      /<img src="([^"]+)"([^>]*)>/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer"><img src="$1"$2></a>',
    );

    return html;
  };

  // Componente para renderizar o conteúdo da mensagem
  const MessageContentRenderer: React.FC<{
    content: MessageContent[];
    isBot: boolean;
  }> = ({ content, isBot }) => {
    const [imgErrors, setImgErrors] = useState<Record<string, boolean>>({});

    const handleImgError = (url: string) => {
      setImgErrors((prev) => ({ ...prev, [url]: true }));
    };

    return (
      <VStack align="stretch" spacing={2}>
        {content.map((item, index) => {
          switch (item.type) {
            case "image":
              return (
                <Box key={index} my={2}>
                  {imgErrors[item.url!] ? (
                    <Link
                      href={item.url}
                      isExternal
                      color={isBot ? "#3182ce" : "white"}
                      textDecoration="underline"
                      wordBreak="break-all"
                    >
                      {item.content}
                    </Link>
                  ) : (
                    <Link href={item.url} isExternal>
                      <Image
                        key={item.url}
                        src={item.url}
                        alt="Imagem"
                        maxW="100%"
                        h="auto"
                        borderRadius="8px"
                        boxShadow="0 2px 8px rgba(0,0,0,0.1)"
                        cursor="pointer"
                        transition="transform 0.2s ease, box-shadow 0.2s ease"
                        crossOrigin="anonymous"
                        onError={() => handleImgError(item.url!)}
                        _hover={{
                          transform: "scale(1.02)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        }}
                      />
                    </Link>
                  )}
                </Box>
              );

            case "link":
              return (
                <Link
                  key={index}
                  href={item.url}
                  isExternal
                  color={isBot ? "#3182ce" : "white"}
                  textDecoration="underline"
                  wordBreak="break-all"
                  _hover={{
                    textDecoration: "none",
                    opacity: 0.8,
                  }}
                >
                  {item.content}
                </Link>
              );

            case "text":
            default:
              return (
                <Box
                  key={index}
                  fontSize={{ base: "xs", md: "sm" }}
                  lineHeight="1.5"
                  dangerouslySetInnerHTML={{
                    __html: markdownToHtml(item.content),
                  }}
                  sx={{
                    "p, span, div": { margin: 0 },
                    strong: { fontWeight: "bold" },
                    a: {
                      color: isBot ? "#3182ce" : "white",
                      textDecoration: "underline",
                      wordBreak: "break-word",
                    },
                    ul: { paddingLeft: "20px" },
                    ol: { paddingLeft: "20px" },
                  }}
                />
              );
          }
        })}
      </VStack>
    );
  };

  // Função para ajustar altura do textarea automaticamente
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;

      // Salvar posição do cursor
      const cursorPosition = textarea.selectionStart;

      // Reset completo da altura
      textarea.style.height = "auto";

      // Forçar recálculo no próximo frame
      requestAnimationFrame(() => {
        if (textarea) {
          const scrollHeight = textarea.scrollHeight;
          const maxHeight = 120; // Máximo de ~4 linhas
          const minHeight = 30; // Mínimo de 1 linha

          // Calcular nova altura
          let newHeight = scrollHeight;

          // Se não há texto, usar altura mínima
          if (!textarea.value.trim()) {
            // newHeight = minHeight;
          } else {
            // Garantir que está dentro dos limites
            newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
          }

          textarea.style.height = `${newHeight}px`;

          // Restaurar posição do cursor
          textarea.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    }
  }, []);

  // Função para fazer scroll para o final das mensagens
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, []);

  // Inicializar chat
  const initializeChat = useCallback(() => {
    let existingChatId = chatServiceInstance.getChatId();

    if (!existingChatId) {
      // Gerar novo chatId se não existir
      existingChatId = chatServiceInstance.generateChatId();
      chatServiceInstance.saveChatId(existingChatId);
    }

    setChatId(existingChatId);

    // Adicionar mensagem inicial se não houver mensagens
    if (messages.length === 0) {
      setMessages([initialMessage]);
    }
  }, [messages.length]);

  // Inicializar mensagens apenas quando o chat for aberto pela primeira vez
  const handleOpenChat = () => {
    setIsChatOpen(true);
    initializeChat();
  };

  useEffect(() => {
    localStorage.setItem("chatbotPosition", JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(true);
    setDragMoved(false);

    const pixelX = position.xPerc * window.innerWidth;
    const pixelY = position.yPerc * window.innerHeight;

    setOffset({
      x: e.clientX - pixelX,
      y: e.clientY - pixelY,
    });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return;
      setDragMoved(true);

      const half = BUTTON_SIZE / 2;
      const newX = Math.max(
        half,
        Math.min(e.clientX - offset.x + half, viewport.width - half),
      );
      const newY = Math.max(
        half,
        Math.min(e.clientY - offset.y + half, viewport.height - half),
      );

      setPosition({
        xPerc: newX / viewport.width,
        yPerc: newY / viewport.height,
      });
    },
    [dragging, offset, viewport, BUTTON_SIZE],
  );

  const handleMouseUp = () => setDragging(false);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev: any) => ({ ...prev })); // força re-render com novas dimensões
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (dragging) {
      // Mouse
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      // Touch (mobile)
      window.addEventListener("touchmove", handleTouchMove);
      window.addEventListener("touchend", handleMouseUp);
    } else {
      // Mouse
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      // Touch
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    }

    return () => {
      // Cleanup geral (IMPORTANTE)
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [dragging, offset]);

  // Scroll automático apenas quando mensagens mudam (não quando digitando)
  useEffect(() => {
    if (isChatOpen && messages.length > 0) {
      // Pequeno delay para garantir que o DOM foi atualizado
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [messages, isChatOpen, scrollToBottom]);

  // Scroll quando loading muda (typing indicator aparece/desaparece)
  useEffect(() => {
    if (isChatOpen && isLoading) {
      const timeoutId = setTimeout(() => {
        scrollToBottom();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, isChatOpen, scrollToBottom]);

  // Apenas ajustar altura do textarea quando input muda (SEM scroll)
  useEffect(() => {
    adjustTextareaHeight();
  }, [inputValue, adjustTextareaHeight]);

  // Scroll inicial quando chat abre
  useEffect(() => {
    if (isChatOpen && messages.length > 0) {
      const timeoutId = setTimeout(() => {
        scrollToBottom();
        adjustTextareaHeight();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [isChatOpen, scrollToBottom, adjustTextareaHeight]);

  // Posicionar cursor no final do input quando chat é reaberto
  useEffect(() => {
    if (isChatOpen && textareaRef.current) {
      const textarea = textareaRef.current;

      const timeoutId = setTimeout(() => {
        // apenas focar
        textarea.focus();

        // forçar scroll pro fim sem alterar cursor
        textarea.scrollTop = textarea.scrollHeight;
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [isChatOpen]);

  // Initialize chat with welcome message when opened
  useEffect(() => {
    if (isChatOpen && messages.length === 0) {
      const welcomeMessage: Message = {
        id: "1",
        text: "Olá, eu sou a Elô, assistente virtual do Grupo Direcional, muito prazer!\n\nVocê precisa de ajuda com qual assunto?",
        isBot: true,
        timestamp: new Date(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isChatOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = inputValue;
    setInputValue("");
    setIsLoading(true);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    try {
      // Obter dados do usuário do localStorage
      const userId = getCookie(cookieKeys.idCorretor) || "";
      const boraUserId = getCookie(cookieKeys.usuarioId) || "";
      const regional = localStorage.getItem("@regional") || "";
      const empresa = localStorage.getItem("@empresa") || "";
      const username = localStorage.getItem("@user") || "";
      const atividade = localStorage.getItem("@atividade") || "";

      const requestData: SendMessageRequest = {
        userId,
        boraUserId,
        chatId,
        regional,
        empresa,
        messageType: "text",
        text: messageText,
        username,
        platform: "web",
        user_type: atividade,
      };

      const response = await chatServiceInstance.sendMessage(requestData);

      // Processar as mensagens de resposta do novo formato
      if (
        response.messages &&
        Array.isArray(response.messages) &&
        response.messages.length > 0
      ) {
        // Criar uma mensagem separada para cada item do array
        const botMessages: Message[] = response.messages.map((msg, index) => ({
          id: `${Date.now()}_${index}`,
          text: msg.content,
          isBot: true,
          timestamp: new Date(),
        }));

        // Adicionar as mensagens uma por uma com um pequeno delay entre elas
        botMessages.forEach((botMessage, index) => {
          setTimeout(() => {
            setMessages((prev) => [...prev, botMessage]);
          }, index * 500); // 500ms de delay entre cada mensagem
        });
      } else {
        // Fallback para formato antigo ou resposta vazia
        const errorMessage: Message = {
          id: `error_${Date.now()}`,
          text: "Desculpe, não consegui processar sua mensagem. Tente novamente.",
          isBot: true,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);

      // Mensagem de erro para o usuário
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: "Desculpe, ocorreu um erro ao processar sua mensagem. Tente novamente em alguns instantes.",
        isBot: true,
        timestamp: new Date(),
      };

      setTimeout(() => {
        setMessages((prev) => [...prev, errorMessage]);
      }, 800);
    } finally {
      // Delay para remover o loading após as mensagens aparecerem
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Em mobile, Enter não envia (usuário pode querer quebrar linha)
    if (e.key === "Enter" && !e.shiftKey && !isTouch) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!dragging) return;

    const touch = e.touches[0];

    let newX = touch.clientX - offset.x;
    let newY = touch.clientY - offset.y;

    const buttonSize = 60; // mesmo tamanho do botão (ou 56 no mobile)

    // Limites horizontais
    newX = Math.max(0, Math.min(newX, window.innerWidth - buttonSize));

    //  Limites verticais
    newY = Math.max(0, Math.min(newY, window.innerHeight - buttonSize));

    setPosition({
      xPerc: newX / window.innerWidth,
      yPerc: newY / window.innerHeight,
    });
  };

  // Limpar chat quando o componente for desmontado (usuário fecha a aba)
  useEffect(() => {
    const handleBeforeUnload = () => {
      chatServiceInstance.clearChatId();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Se o usuário não estiver autenticado, não renderizar nada
  if (!isAuthenticated || accessChatBot === false) {
    return null;
  }

  return (
    <Portal>
      {/* Botão flutuante */}
      {!isChatOpen && (
        <Box
          w="fit-content"
          position="fixed"
          left={`${position.xPerc * 100}%`}
          top={`${position.yPerc * 100}%`}
          transform="translate(-50%, -50%)"
          zIndex={9999}
          cursor={dragging ? "grabbing" : "grab"}
          onMouseDown={handleMouseDown}
          onMouseUp={() => {
            if (!dragMoved) handleOpenChat();
          }}
          onTouchStart={(e) => {
            setDragging(true);
            setDragMoved(false);

            const touch = e.touches[0];

            const pixelX = position.xPerc * window.innerWidth;
            const pixelY = position.yPerc * window.innerHeight;

            setOffset({
              x: touch.clientX - pixelX,
              y: touch.clientY - pixelY,
            });
          }}
          style={{ touchAction: "none", userSelect: "none" }}
        >
          <ScaleFade in={!isChatOpen}>
            <Button
              bg={theme.primary}
              borderRadius="full"
              width={{ base: "56px", md: "60px" }}
              height={{ base: "56px", md: "60px" }}
              boxShadow="lg"
              _hover={{
                transform: "scale(1.1)",
                boxShadow: "xl",
              }}
              transition="all 0.2s"
            >
              {/* <MdOutlineChatBubbleOutline color="white" fontSize="40px" /> */}
              <Avatar
                size="lg"
                name="Elô"
                animation={`${bounceIn} 0.3s ease-out 0.2s both`}
                src={AvatarEloAzul}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              />
            </Button>
          </ScaleFade>
        </Box>
      )}

      {/* Chat Window com animação personalizada */}
      {isChatOpen && (
        <Box
          position="fixed"
          w={"fit-content"}
          right={{ base: 0, md: "20px" }}
          bottom={{ base: 0, md: "20px" }}
          borderRadius={{ base: 0, md: "lg" }}
          zIndex={9999}
          animation={`${slideUpAndFade} 0.3s ease-out`}
        >
          <Box
            width={{
              base: "100vw",
              sm: "350px",
              md: "400px",
            }}
            maxW="100vw"
            height={{
              base: "calc(100dvh - 16px)",
              sm: "450px",
            }}
            maxHeight={{ base: "none", sm: "450px" }}
            bg={bgColor}
            borderRadius="lg"
            boxShadow="2xl"
            border="1px"
            borderColor={borderColor}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            transform="translateZ(0)"
          >
            {/* Header com animação */}
            <Flex
              bg={"white"}
              color="white"
              p={{ base: 3, md: 4 }}
              align="center"
              justify="space-between"
              minHeight="60px"
              animation={`${fadeInUp} 0.4s ease-out 0.1s both`}
              boxShadow={"lg"}
            >
              <HStack spacing={3}>
                <Avatar
                  size="sm"
                  name="Elô"
                  animation={`${bounceIn} 0.6s ease-out 0.2s both`}
                  bg={theme.disable}
                  boxShadow={"lg"}
                  src={AvatarElo}
                />
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" fontSize="sm" color={theme.text}>
                    Elô
                  </Text>
                  <Text fontSize="xs" opacity={0.8} color={theme.text}>
                    {isLoading ? "digitando..." : "Assistente Virtual"}
                  </Text>
                </VStack>
              </HStack>

              <Box
                aria-label="Fechar chat"
                w={"fit-content"}
                h={"fit-content"}
                onClick={handleCloseChat}
                cursor={"pointer"}
                _hover={{
                  transform: "rotate(90deg)",
                }}
                transition="all 0.2s"
                mr={"5px"}
              >
                <MdOutlineClose color={theme.disable} fontSize={"22px"} />
              </Box>
            </Flex>

            {/* Messages com animação */}
            <VStack
              ref={messagesContainerRef}
              flex={1}
              spacing={3}
              p={{ base: 3, md: 4 }}
              align="stretch"
              overflowY="auto"
              animation={`${fadeInUp} 0.5s ease-out 0.2s both`}
              css={{
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E0",
                  borderRadius: "24px",
                },
              }}
            >
              {messages.map((message, index) => (
                <Flex
                  key={message.id}
                  justify={message.isBot ? "flex-start" : "flex-end"}
                  animation={`${fadeInUp} 0.4s ease-out both`}
                  w="100%"
                >
                  <Box
                    w="fit-content"
                    minW="40px"
                    maxW={{ base: "85%", md: "80%" }}
                    bg={message.isBot ? botMessageBg : userMessageBg}
                    color={message.isBot ? textColor : "white"}
                    p={{ base: 2, md: 3 }}
                    borderRadius="lg"
                    borderBottomLeftRadius={message.isBot ? "sm" : "lg"}
                    borderBottomRightRadius={message.isBot ? "lg" : "sm"}
                    _hover={{
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                    }}
                    transition="all 0.2s"
                    wordBreak="break-word"
                    overflowWrap="break-word"
                  >
                    {message.isBot ? (
                      <MessageContentRenderer
                        content={parseMessageContent(message.text)}
                        isBot={message.isBot}
                      />
                    ) : (
                      <Text
                        fontSize={{ base: "xs", md: "sm" }}
                        lineHeight="1.4"
                        dangerouslySetInnerHTML={{
                          __html: message.text.replace(/\n/g, "<br>"),
                        }}
                      />
                    )}
                  </Box>
                </Flex>
              ))}

              {/* Indicador de digitação estilo WhatsApp */}
              {isLoading && (
                <Flex
                  justify="flex-start"
                  animation={`${fadeIn} 0.3s ease-out`}
                  w="100%"
                >
                  <Box
                    bg={botMessageBg}
                    p={{ base: 2, md: 3 }}
                    borderRadius="lg"
                    borderBottomLeftRadius="sm"
                    borderBottomRightRadius="lg"
                    w="fit-content"
                    minW="60px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <TypingIndicator />
                  </Box>
                </Flex>
              )}

              <div ref={messagesEndRef} />
            </VStack>

            {/* Input com animação - Agora usando Textarea expansível */}
            <Box
              borderTop="1px"
              borderColor={"gray.200"}
              animation={`${fadeInUp} 0.4s ease-out 0.3s both`}
              p={"5px"}
              position="relative"
            >
              <Textarea
                ref={textareaRef}
                placeholder="Digite sua mensagem"
                value={inputValue}
                onChange={handleInputChange}
                // onKeyPress={handleKeyPress}
                onKeyDown={handleKeyDown}
                resize="none"
                rows={1}
                minH={"30px"}
                maxH="120px"
                maxW="92%"
                px={2}
                py={2}
                fontSize={{ base: "12px", md: "14px" }}
                border="none"
                _focus={{
                  boxShadow: "none",
                  border: "none",
                }}
                _hover={{
                  border: "none",
                }}
                transition="all 0.2s"
                disabled={isLoading}
                overflowY="auto"
                css={{
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#CBD5E0",
                    borderRadius: "24px",
                  },
                }}
              />

              {/* Botão de enviar posicionado absolutamente */}
              <Box
                w={"fit-content"}
                position="absolute"
                right="8px"
                bottom="8px"
                aria-label="Enviar mensagem"
                onClick={handleSendMessage}
                cursor={
                  isLoading || !inputValue.trim() ? "not-allowed" : "pointer"
                }
                opacity={isLoading || !inputValue.trim() ? 0.5 : 1}
                _hover={{
                  transform:
                    isLoading || !inputValue.trim()
                      ? "none"
                      : "scale(1.1) rotate(15deg)",
                }}
                transition="all 0.2s"
                p={1}
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg="transparent"
                zIndex={1}
              >
                {isLoading ? (
                  <Spinner size="sm" color={theme.error50} />
                ) : (
                  <FiSend color={theme.error50} fontSize={"20px"} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Portal>
  );
};

export default ChatBot;
